import { FC, ReactElement, useMemo, useState } from "react";
import { Range } from "react-date-range";
import { format, startOfYear } from "date-fns";
import { CircularProgress, Grid, Stack, Typography } from "@mui/material";

import { DateRangeFilter, SelectClientBanner } from "@/components";
import { CountriesCard, ProgramsCard } from "./components";
import { AdvanceFilters } from "../first-sale/components";
import { TariffCard } from "../../../analytics/components";
import {
  useGetDashboardTariffHtsQuery,
  useGetDashboardUpdateDateQuery,
  useGetPotentialDashboardCountriesQuery,
  useGetPotentialDashboardProgramsQuery,
  useGetPotentialDashboardTariffCodesQuery,
} from "@/services";
import { useAppSelector } from "@/store";
import { formatDateOrTime } from "@/utils";
import { roles } from "@/roles";

const TARIFF_CODE_PAGE_SIZE = 5;
const POTENTIAL_SAVING_PAGE_SIZE = 5;

const PreferentialOrigin: FC = (): ReactElement => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient, areClientsLoading } = useAppSelector(
    (state) => state.client
  );
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const [pageNo, setPageNo] = useState<number>(1);
  const [savingPageNo, setSavingPageNo] = useState<number>(1);
  const [dateFilter, setDateFilter] = useState<
    Pick<Range, "startDate" | "endDate">
  >({
    startDate: startOfYear(new Date()),
    endDate: new Date(),
  });
  const [dutyType, setDutyType] = useState<Attribute>("" as Attribute);

  const canSelectClients = useMemo(() => {
    const allowedRoles = [
      roles.SPOWNER,
      roles.SPPARTNER,
      roles.TDADMIN,
    ] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const commonFilters = useMemo(() => {
    return {
      dateFrom: format(dateFilter.startDate ?? new Date(), "yyyy-MM-dd"),
      dateTo: format(dateFilter.endDate ?? new Date(), "yyyy-MM-dd"),
      clientId: selectedClient?._id,
      ...(isTDAdminTCD && {
        tenantId: selectedTenant?._id,
      }),
      ...(dutyType && { attribute: dutyType }),
    };
  }, [dateFilter, selectedClient, dutyType, selectedTenant]);

  const commonOptions = useMemo(() => {
    if (isTDAdminTCD) {
      return {
        skip: !selectedTenant,
        refetchOnMountOrArgChange: true,
      };
    }
    return {
      skip: canSelectClients && !selectedClient,
      refetchOnMountOrArgChange: true,
    };
  }, [canSelectClients, selectedClient, selectedTenant]);

  const { data: lastUpdated, isFetching: isFetchingDate } =
    useGetDashboardUpdateDateQuery(
      {
        params: {
          clientId: selectedClient?._id,
          ...(isTDAdminTCD && {
            tenantId: selectedTenant?._id,
          }),
        },
      },
      commonOptions
    );

  const {
    data: tariffData,
    isLoading: isTariffLoading,
    isFetching: isTariffFetcing,
  } = useGetDashboardTariffHtsQuery(
    {
      dashboardVisibility: {
        ...commonFilters,
        minThreshold: 0,
        pageNo,
        pageSize: TARIFF_CODE_PAGE_SIZE,
      },
    },
    { ...commonOptions }
  );

  const {
    data: tariffCodeData,
    isLoading: isTariffCodesLoading,
    isFetching: isTariffCodesFetching,
  } = useGetPotentialDashboardTariffCodesQuery(
    {
      dashboardPotential: {
        ...commonFilters,
        pageNo: savingPageNo,
        pageSize: POTENTIAL_SAVING_PAGE_SIZE,
      },
    },
    commonOptions
  );

  const {
    data: programsData,
    isLoading: isProgramsLoading,
    isFetching: isProgramsFetching,
  } = useGetPotentialDashboardProgramsQuery(
    {
      dashboardPotential: {
        ...commonFilters,
        pageNo: savingPageNo,
        pageSize: POTENTIAL_SAVING_PAGE_SIZE,
      },
    },
    commonOptions
  );

  const {
    data: countriesData,
    isLoading: isCountriesLoading,
    isFetching: isCountriesFetching,
  } = useGetPotentialDashboardCountriesQuery(
    {
      dashboardPotential: {
        ...commonFilters,
        pageNo: savingPageNo,
        pageSize: POTENTIAL_SAVING_PAGE_SIZE,
      },
    },
    commonOptions
  );

  const isAnyApiLoading = useMemo(() => {
    return [
      isFetchingDate,
      isTariffCodesLoading,
      isTariffLoading,
      isProgramsLoading,
      isCountriesLoading,
    ].some(Boolean);
  }, [
    isFetchingDate,
    isTariffCodesLoading,
    isTariffLoading,
    isProgramsLoading,
    isCountriesLoading,
  ]);

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return (
      <SelectClientBanner/>
    )
  }

  return (
    <>
      <Grid container alignItems="center" mt={3.5} mb={3}>
        <Grid item xs={12} md={6}>
          <Stack>
            <Typography
              variant="h5"
              fontSize={32}
              color="neutral.1100"
              lineHeight="38px"
            >
              Free Trade Agreement
            </Typography>
            <Typography
              variant="body1"
              color="grey.100"
              lineHeight="22px"
              fontWeight={400}
            >
              Last Updated {formatDateOrTime(lastUpdated ?? new Date(), "both")}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              justifyContent: {
                xs: "center",
                sm: "flex-end",
              },
              marginTop: {
                xs: 2,
                sm: 0,
              },
            }}
          >
            <DateRangeFilter value={dateFilter} onSelectDate={setDateFilter} />
            <AdvanceFilters
              appliedFilters={{ dutyType }}
              onFiltersChange={({ dutyType: updatedDutyType }) =>
                setDutyType(updatedDutyType as Attribute)
              }
            />
          </Stack>
        </Grid>
      </Grid>
      {isAnyApiLoading || areClientsLoading ? (
        <Stack flex={1} justifyContent="center" alignItems="center">
          <CircularProgress />
        </Stack>
      ) : (
        <Grid container spacing={2.75}>
          <Grid item xs={12} md={6}>
            <TariffCard
              mode="chart"
              data={tariffData?.data ?? []}
              totalPage={Math.ceil(
                (tariffData?.meta.countDocs ?? 0) / TARIFF_CODE_PAGE_SIZE
              )}
              currentPage={pageNo}
              isLoading={isTariffFetcing}
              onPageChange={setPageNo}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TariffCard
              mode="chart"
              title="Saving Potential per Classification"
              getCategories={() =>
                tariffCodeData?.data
                  .map(({ htsNumber }) => Math.round(Number(htsNumber)))
                  .filter(Boolean) ?? []
              }
              getSeries={() =>
                tariffCodeData?.data
                  ? [
                      {
                        name: "Potential Saving",
                        data: tariffCodeData.data
                          .map(({ potentialSaving }) => potentialSaving)
                          .filter(Boolean),
                      },
                    ]
                  : []
              }
              totalPage={Math.ceil(
                (tariffCodeData?.meta.countDocs ?? 0) /
                  POTENTIAL_SAVING_PAGE_SIZE
              )}
              currentPage={savingPageNo}
              isLoading={isTariffCodesFetching}
              onPageChange={setSavingPageNo}
              colors={["#0B494B"]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ProgramsCard
              data={programsData?.data ?? []}
              isLoading={isProgramsFetching}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CountriesCard
              isLoading={isCountriesFetching}
              data={countriesData?.data ?? []}
            />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default PreferentialOrigin;
